/* eslint-disable no-useless-escape */
import moment from "moment";
import {
	find,
	filter,
	some
} from "lodash";
import template from "./number.html";
import "./number.less";

import {
	FEATURE_TYPES,
	EVENT_FILTER_CURRENT_OPTIONS,
	EVENT_FILTER_ALL_OPTIONS,
	EVENT_SET_VALUE,
	EVENT_CLEAR_VALUE,
	EVENT_UNCLEAR_VALUE,
	EVENT_FIELD_DISABLE,
	EVENT_FIELD_ENABLE,
	EVENT_SET_FEATURE_DATA,
	EVENT_CLEAR_ROW,
	EVENT_HOTKEY_LOCK,
	EVENT_HANDLE_SUCCESSOR_LOCK,
	EVENT_DISABLE_SUCCESSOR_LOCK,
	EVENT_ENABLE_SUCCESSOR_LOCK,
	EVENT_REVERT_VALUES,
	EVENT_VALUE_CHANGED,
	EVENT_SET_FORM_PRISTINE
} from "constants.js";
import { IS_NOT_AVAILABLE_OPTIONS } from "../../../../constants";
import { isValueNullish } from "../../../../utils";

export class Controller {
	selection = {};
	lookups = [];

	constructor($scope, eventBus, featureHelper, toastr, config, $timeout, $logger) {
		this._scope = $scope;
		this.featureHelper = featureHelper;
		this._toastr = toastr;
		this.config = config;
		this.$timeout = $timeout;
		this.$logger = $logger;

		this.isNumber = /^(\-)?(\d*(\.|\.\d+)?)?$/;
		this.trailingComma = /^[1-9]\d*(\.\d+)?$/;
		this.incrementingStep = 1;
		this.digitCount = 0;

		this.MAX_FIELD_VALUE = 999999;
	}

	$onInit() {
		this._eventBus = this.reportScope.events;
		const ctrl = this;
		this.isFocused = false;
		this.field.rangeErrorRepeatIds = this.field.rangeErrorRepeatIds || {};
		delete this.field.rangeErrorRepeatIds[this.repeatId];

		this._unregister = this.reportFormCtrl.registerFieldController(this);

		this.setOptionIdType();
		this.initialValue = find(this.values, v => v.FieldId === this.field.Id);
		this.isNotAvailable = !!this.initialValue?.IsNotAvailable;
		this.options = this.field.Data;
		this.setValue();
		this.setFeatures();

		if(this.field.IncrementingStep) {
			this.incrementingStep = this.field.IncrementingStep;
			const decimalSplit = `${this.field.IncrementingStep}`.split(".");
			this.digitCount = decimalSplit[1] ? decimalSplit[1].length : 0;
		}

		const filterOptions = (e, params) => {
			this.enableField();
			ctrl.field.placeHolder = null;

			if(params.clear) {
				this.selection = {};
			}

			ctrl.setOptions(params.ids);
			if(params.readonly) {
				this.disableField();
			}

			if((this.selection && this.selection.unit) || params.clear) {
				this.setUnitSelection(this.selection.unit);

				if(this.lastUnitId !== this.selection?.unit?.Id) {
					this.triggerUnitChanged();
				}
			}

			this.lastUnitId = this.selection?.unit?.Id;
		};

		this._eventBus.register(EVENT_FILTER_CURRENT_OPTIONS(this.field.Id, this.repeatId), filterOptions, this._scope);

		this._eventBus.register(EVENT_FILTER_ALL_OPTIONS(this.field.Id, this.repeatId), filterOptions, this._scope);

		this._eventBus.register(EVENT_SET_VALUE(this.field.Id), (e, params) => ctrl.setValue(params.value), this._scope);

		this._eventBus.register(EVENT_HOTKEY_LOCK, () => ctrl.isFocused && ctrl.field.IsLockable && (ctrl.field.isLocked = !ctrl.field.isLocked), this._scope);

		this._eventBus.register(EVENT_HANDLE_SUCCESSOR_LOCK(this.field.Id), (e, params) => {
			this.field.isFieldLockable = params.enable && this.field.IsLockable;
			if(this.field.isFieldLockable !== true) {
				this.field.isLocked = false;
				this._eventBus.trigger(EVENT_DISABLE_SUCCESSOR_LOCK(this.field.Id));
			}
			else {
				this._eventBus.trigger(EVENT_ENABLE_SUCCESSOR_LOCK(this.field.Id));
			}
		}, this._scope);

		this._eventBus.register(EVENT_CLEAR_VALUE(this.field.Id, this.repeatId), (e, params) => {
			if(!this.cleared && !!this.selection?.number && params.isInitializing !== true) {
				this.cleared = {
					selection: { ...this.selection }
				};
			}
			this.selection.number = undefined;
		}, this._scope);

		this._eventBus.register(EVENT_UNCLEAR_VALUE(this.field.Id), () => {
			if(!!this.cleared) {
				this.selection = this.cleared.selection;

				this.cleared = undefined;
			}
		}, this._scope);

		this._eventBus.register(EVENT_CLEAR_ROW(this.repeatId), () => {
			this.selection.number = undefined;
		}, this._scope);

		this._eventBus.register(EVENT_FIELD_DISABLE(this.field.Id), () => this.disableField(), this._scope);
		this._eventBus.register(EVENT_FIELD_ENABLE(this.field.Id), () => this.enableField(), this._scope);
		this._eventBus.register(EVENT_REVERT_VALUES, () => {
			this.revertValue();
		}, this._scope);

		this._eventBus.register(EVENT_SET_FORM_PRISTINE, () => {
			this.setPristine();
		}, this._scope);
	}

	$onDestroy() {
		this._unregister();
	}

	getValidationStyling() {
		return this.field.hasValidationError ? "validation-error"
			: this.field.hasValidationWarning ? "validation-warning" : "";
	}

	revertValue() {
		this.selection.number = this.initialValue && this.initialValue.Number;
		this.selection.unit = this.initialValue && find(this.options, o => o.Id === this.getOptionId(this.initialValue));
		if(this.selection.unit === undefined) {
			this.setDefaultUnit();
		}
	}

	setOptions(ids) {
		this.options = ids ?
			filter(this.field.Data, (d) => some(ids, (i) => d.Id === i)) :
			this.field.Data;

		if(this.selection.unit?.Id && !some(this.options, (o) => o.Id === this.selection.unit.Id)) {
			this.selection.unit = undefined;
		}

		this.setDefaultUnit();
	}

	isDeactivated(date) {
		return !!date && moment(date).isBefore(moment());
	}

	getVisibleUnits() {
		const selectedUnitId = this.selection.unit?.Id;

		return this.options.filter(unit => unit.Id === selectedUnitId || !this.isDeactivated(unit.DeactivatedAt));
	}

	disableField() {
		this.disableInput();
		this.field.isDisabled = true;
		this.lookups.forEach(l => l.disabled = true);
	}

	disableInput() {
		this.isInputDisabled = true;
	}

	enableField() {
		this.enableInput();
		this.field.isDisabled = false;
		this.field.placeHolder = null;
		this.lookups.forEach(l => l.disabled = false);
	}

	enableInput() {
		this.isInputDisabled = false;
	}

	getCurrentValue(isApiCall) {
		if((!!this.selection.number && !isNaN(this.selection.number)) && !this.selection.unit && isApiCall) {
			this._toastr.error(`Please select a Unit for ${this.field.Name}.`);
			throw new Error(`Unit not found for ${this.field.Name}`);
		}

		let result = this.buildResult();
		if(result && result.Number && ((result.Number > this.MAX_FIELD_VALUE) || result.Number < (-1 * this.MAX_FIELD_VALUE))) {
			if(this.field.rangeErrorRepeatIds[this.repeatId] !== true) {
				this._toastr.error(`Number field cannot accept a number greater than ${this.MAX_FIELD_VALUE} or less than -${this.MAX_FIELD_VALUE}`);

				this.selection.number = undefined;
				this.field.rangeErrorRepeatIds[this.repeatId] = true;
				setTimeout(() => {
					delete this.field.rangeErrorRepeatIds[this.repeatId];
				}, 5000);
			}

			throw new Error(`Number field out of range for ${this.field.Name}`);
		}
		else {
			delete this.field.rangeErrorRepeatIds[this.repeatId];
		}

		if(!isApiCall) {
			if(result?.[this.optionIdType] === undefined) {
				result = {
					...(result ?? {}),
					FieldId: this.field.Id
				};
				result.hasResultForRuleRun = false;
				if(this.selection.unit || this.isNotAvailable) {
					result[this.optionIdType] = this.selection?.unit?.Id;
					result.hasResultForRuleRun = true;
				}
			}
			result.factor = (this.selection.unit?.Factor) || 1;
			result.isDirty = this.isDirty(result);
		}

		return result && !this.field.hasRangeError ? [result] : [];
	}

	isDirty(result) {
		return this.field.IsNotReported !== true
			&& this.field.IsHidden !== true
			&& (((result && result.Number) != (this.initialValue && this.initialValue.Number)
				|| this.getOptionId(result) != this.getOptionId(this.initialValue)));
	}

	buildResult() {
		if(this.isNotAvailable) {
			return {
				FieldId: this.field.Id,
				IsNotAvailable: true,
				hasFullResult: true
			};
		}

		let result = this.selection.number !== null
			&& this.selection.number !== undefined
			&& this.selection.number !== ""
			&& !isNaN(this.selection.number)
			&& this.selection.unit?.Id ?
			{
				FieldId: this.field.Id,
				Number: parseFloat(this.selection.number),
				[this.optionIdType]: this.selection.unit.Id,
				hasFullResult: true
			} :
			undefined;

		if(this.repeatId) {
			result = {
				...(result ?? {}),
				FieldId: this.field.Id,
				repeatId: this.repeatId,
				hasFullResult: !!result?.hasFullResult
			};
		}

		return result;
	}

	setFeatures() {
		const mapFeature = find(this.field.features, { Type: FEATURE_TYPES.mapPoint });

		if(mapFeature) {
			this.lookups.push({
				name: mapFeature.Type,
				fieldId: this.field.Id,
				disabled: this.field.isReadonly || this.field.isDisabled
			});

			this._eventBus.register(EVENT_SET_FEATURE_DATA(mapFeature.Type), (event, params) => {
				const lookup = find(this.lookups, l => l.name === mapFeature.Type);
				lookup.data = params.geoJsons;
				lookup.fields = params.locationFields;
			}, this._scope);
		}

		this.setupIsNotAvailableFeature();
	}

	setupIsNotAvailableFeature() {
		const isNotAvailableFeatureEnabled = this.featureHelper.checkNotAvailable(this.field, IS_NOT_AVAILABLE_OPTIONS.always);

		if(isNotAvailableFeatureEnabled) {
			const feature = this.featureHelper.getNotAvailableFeature(this.field, this.isNotAvailable, (selection) => this.setNotAvailable(selection));
			this.setNotAvailable(this.isNotAvailable);
			this.lookups.push(feature);
		}
	}

	setNotAvailable(turnOn) {
		if(turnOn !== undefined && turnOn === this.isNotAvailable) {
			if(turnOn === true) {
				this.disableInput();
			}
			return;
		}

		if(!this.selection) {
			this.selection = {
				unit: this.field.Data[0]
			};
		}

		this.isNotAvailable = turnOn;

		if(this.isNotAvailable) {
			this.selection = {
				...this.selection,
				number: undefined
			};
			this.value = undefined;
			this.disableInput();
		}
		else if(!this.field.isReadonly) {
			this.enableInput();
		}
	}

	handleLookupSelection(lookup) {
		if(lookup.action) {
			lookup.action(lookup.selection);
		}
	}

	setPristine() {
		this.initialValue = this.field.IsDetail ? undefined : this.buildResult();
	}

	setUnitSelection(unit, forceValueTrigger) {
		this.selection.unit = unit;
		this.field.focus = true;

		const isDirtyUnit = (this.field.visible !== false || this.initialValue !== undefined)
			&& (this.getOptionId(this.initialValue) !== unit?.Id);

		this.setFieldDirty(this.field.Id, isDirtyUnit);

		if(forceValueTrigger) {
			this.triggerUnitChanged();
		}
	}

	triggerUnitChanged() {
		const params = {
			field: this.field,
			value: this.selection.unit
		};

		this._eventBus.trigger(EVENT_VALUE_CHANGED(this.field.Id), params);
	}

	setValue(value) {
		this.value = value ? value : find(this.values, v => v.FieldId === this.field.Id);
		const optionId = this.getOptionId(this.value);

		if(!isValueNullish(this.value?.Number) && !isValueNullish(optionId)) {
			this.selection = {
				number: parseFloat(parseFloat(this.value.Number).toFixed(this.field.RoundingPoint)),
				unit: find(this.field.Data, d => d.Id === optionId)
			};
		}
		this.setDefaultUnit();
	}

	setOptionIdType() {
		const fieldType = this.field.Type?.Code;
		const isEntityType = fieldType === "ENTITY" || fieldType === "ENTITYCONTACT" || this.field.Type?.IsEntity === true;

		this.optionIdType = isEntityType || !!this.field.EntityQueryConfiguration ? "EntityId" : "DataId";
	}

	getOptionId(value) {
		return value?.[this.optionIdType] ?? value?.DataId ?? value?.EntityId;
	}

	setDefaultUnit() {
		if(this.selection.unit !== undefined) {
			return;
		}

		const activeOptions = this.options.filter(unit => !this.isDeactivated(unit.DeactivatedAt));

		if(activeOptions.length === 1) {
			this.selection.unit = activeOptions[0];

			return;
		}
		this.selection.unit = activeOptions.find((d) => d.IsDefault);
	}
}

export default angular
	.module("vesl.components.report-form.number", [])
	.component("number", {
		template,
		controller: Controller,
		bindings: {
			field: "<",
			values: "<",
			repeatId: "<",
			pivotRow: "=",
			pivotColumn: "=",
			setFieldDirty: "=",
			constraintMessage: "=?"
		},
		require: {
			reportFormCtrl: "^reportForm",
			reportScope: "^^reportScope"
		}
	}).name;
